import { useEffect, useState } from "react";
import { Box, Container, Typography, IconButton, Skeleton, Alert } from "@mui/material";
import Slider from "react-slick";
import {
  ArrowBackIosNew,
  ArrowForwardIos,
  GroupsRounded,
  StarRounded,
  VerifiedRounded,
  WorkspacePremiumRounded,
} from "@mui/icons-material";
import { topClientApi } from "../../API/Api";
import ClientItem from "./ClientItem";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const tokens = {
  ink: "#12242E",
  teal: "#0F8B8D",
  tealDark: "#0A5F60",
  line: "rgba(18,36,46,0.10)",
  headingFont: "'Sora', sans-serif",
};

// Shared keyframes, applied via sx={{ ...fadeInUp, animation: "fadeInUp .5s ease both" }}
const fadeInUp = {
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};
const fadeIn = {
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
};

const SKELETON_COUNT = 4;

// NOTE: placeholder numbers — swap these for real figures (ideally pulled
// from your API alongside topClientApi) once you have them.
const TRUST_STATS = [
  { icon: GroupsRounded, value: "500+", label: "Clients Served" },
  { icon: StarRounded, value: "4.9/5", label: "Average Rating" },
  { icon: VerifiedRounded, value: "100%", label: "Verified Businesses" },
  { icon: WorkspacePremiumRounded, value: "10+ yrs", label: "Industry Experience" },
];

const PrevArrow = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    aria-label="Previous"
    sx={{
      position: "absolute",
      top: "50%",
      left: { xs: -4, md: -22 },
      zIndex: 2,
      transform: "translateY(-50%)",
      bgcolor: "#fff",
      border: "1px solid",
      borderColor: tokens.line,
      boxShadow: "0 4px 14px rgba(18,36,46,0.08)",
      transition: "all 0.2s ease",
      "&:hover": {
        bgcolor: tokens.teal,
        borderColor: tokens.teal,
        color: "#fff",
        transform: "translateY(-50%) scale(1.05)",
      },
      "&:active": {
        bgcolor: tokens.tealDark,
        borderColor: tokens.tealDark,
      },
    }}
  >
    <ArrowBackIosNew fontSize="small" />
  </IconButton>
);

const NextArrow = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    aria-label="Next"
    sx={{
      position: "absolute",
      top: "50%",
      right: { xs: -4, md: -22 },
      zIndex: 2,
      transform: "translateY(-50%)",
      bgcolor: "#fff",
      border: "1px solid",
      borderColor: tokens.line,
      boxShadow: "0 4px 14px rgba(18,36,46,0.08)",
      transition: "all 0.2s ease",
      "&:hover": {
        bgcolor: tokens.teal,
        borderColor: tokens.teal,
        color: "#fff",
        transform: "translateY(-50%) scale(1.05)",
      },
      "&:active": {
        bgcolor: tokens.tealDark,
        borderColor: tokens.tealDark,
      },
    }}
  >
    <ArrowForwardIos fontSize="small" />
  </IconButton>
);

const TopClientConnect = () => {
  const [clientData, setClientData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(topClientApi);
        const result = await res.json();
        if (cancelled) return;

        const data = result?.data ?? result;
        setClientData(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          console.error("Fetch failed:", err);
          setError("Couldn't load clients right now.");
          setClientData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const settings = {
    dots: false,
    infinite: clientData.length > 3,
    speed: 500,
    slidesToShow: Math.min(3, clientData.length || 3),
    slidesToScroll: 1,
    autoplay: clientData.length > 3,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: Math.min(2, clientData.length || 2) } },
      { breakpoint: 640, settings: { slidesToShow: 1, arrows: false } },
    ],
  };

  return (
    <Box sx={{ width: "100%", py: { xs: 5, md: 7 },  bgcolor: "#FAFBFC" }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 4, md: 5 },
            ...fadeInUp,
            animation: "fadeInUp .5s ease both",
          }}
        >
          {/*
          <Typography
            variant="overline"
            sx={{ color: tokens.teal, fontWeight: 700, letterSpacing: 1.5 }}
          >
            Trusted Partnerships
          </Typography>
           <Typography
            variant="h4"
            sx={{
              fontFamily: tokens.headingFont,
              fontWeight: 700,
              color: tokens.ink,
              mt: 0.5,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            Who Trust Us
          </Typography>
          
          <Typography
            variant="body1"
            sx={{ color: "rgba(18,36,46,0.65)", mt: 1, maxWidth: 520, mx: "auto" }}
          >
            We're proud to work alongside ambitious brands and businesses of
            every size.
          </Typography> */}
        </Box>

        {/* <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: { xs: 2, md: 3 },
            mb: { xs: 5, md: 6 },
          }}
        >
          {TRUST_STATS.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Box
                key={stat.label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 1,
                  py: { xs: 2, md: 2.5 },
                  px: 1.5,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: tokens.line,
                  bgcolor: "#fff",
                  transition: "all 0.25s ease",
                  ...fadeInUp,
                  animation: "fadeInUp .5s ease both",
                  animationDelay: `${index * 0.08}s`,
                  "&:hover": {
                    borderColor: tokens.teal,
                    boxShadow: "0 10px 24px rgba(18,36,46,0.08)",
                    transform: "translateY(-3px)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${tokens.teal}, ${tokens.tealDark})`,
                    color: "#fff",
                    boxShadow: `0 6px 16px rgba(15,138,141,0.35)`,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Typography
                  sx={{
                    fontFamily: tokens.headingFont,
                    fontWeight: 700,
                    color: tokens.ink,
                    fontSize: { xs: "1.1rem", md: "1.35rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(18,36,46,0.6)", fontWeight: 500 }}
                >
                  {stat.label}
                </Typography>
              </Box>
            );
          })}
        </Box> */}

        {error && (
          <Alert severity="error" sx={{ maxWidth: 480, mx: "auto", mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ position: "relative", px: { xs: 1, md: 4 } }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                overflow: "hidden",
                ...fadeIn,
                animation: "fadeIn .4s ease both",
              }}
            >
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <Box key={`client-skeleton-${i}`} sx={{ flex: "1 0 25%", px: 1 }}>
                  <Skeleton
                    variant="rounded"
                    height={100}
                    sx={{ borderRadius: 2 }}
                  />
                </Box>
              ))}
            </Box>
          ) : clientData.length > 0 ? (
            <Slider {...settings}>
              {clientData.map((client, index) => (
                <Box
                  key={client.refer}
                  sx={{
                    px: 1.5,
                    mt:2,
                    ...fadeInUp,
                    animation: "fadeInUp .5s ease both",
                    animationDelay: `${Math.min(index, 6) * 0.06}s`,
                  }}
                >
                  <ClientItem clientData={client} />
                </Box>
              ))}
            </Slider>
          ) : (
            !error && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 4 }}
              >
                No clients to show yet.
              </Typography>
            )
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default TopClientConnect;